import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchAllMetaPages, fetchMetaAdPreview, fetchMetaAds } from '../../services/meta-api.js';
import { buildMarketingSignals, linkClickMetrics, summarizeAdInsights } from './meta-growth-intelligence.js';
import MetaDailyTrend from './MetaDailyTrend.jsx';
import MetaCreativeStudio from './MetaCreativeStudio.jsx';
import { WorkbenchHead, amount, count, percentage } from './MetaWorkbenchUi.jsx';
import './MetaGrowthWorkbench.css';

export default function MetaGrowthWorkbench({
  campaignPerformance, adInsights, selectedInsights, campaign, adset, ad,
  currency, range, accountId, unavailableAdLevel
}) {
  const [selectedAdId, setSelectedAdId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');
  const [catalogStale, setCatalogStale] = useState(false);

  const [focus, setFocus] = useState('all');
  const adRequestId = useRef(0);
  const signals = useMemo(() =>
    buildMarketingSignals(campaignPerformance), [campaignPerformance]
  );
  const ads = useMemo(() =>
    summarizeAdInsights(adInsights, { campaign, adset, ad }), [adInsights, campaign, adset, ad]
  );
  const visibleAds = showAll ? ads : ads.slice(0, 25);
  const linked = useMemo(() => linkClickMetrics(selectedInsights), [selectedInsights]);
  const filteredSignals = signals.filter((item) => focus === 'all' || item.level === focus);
  useEffect(() => {
    adRequestId.current += 1;
    setSelectedAdId(''); setPreview(null); setPreviewError('');
  }, [campaign, adset, ad, range.dateFrom, range.dateTo]);

  async function inspect(adId) {
    const id = ++adRequestId.current;
    setSelectedAdId(adId);
    setPreview(null); setPreviewError(''); setPreviewLoading(true);
    try {
      const data = await fetchMetaAdPreview(adId);
      if (adRequestId.current === id) setPreview(data);
    } catch (e) {
      if (adRequestId.current === id) setPreviewError(e.message || 'No fue posible leer el anuncio.');
    } finally {
      if (adRequestId.current === id) setPreviewLoading(false);
    }
  }
  async function loadCatalog() {
    setCatalogLoading(true); setCatalogError('');
    try {
      let cached = false;
      const all = await fetchAllMetaPages(fetchMetaAds, {}, 100, (page) => { if (page.stale) cached = true; });
      setCatalog(all);
      setCatalogStale(cached);
    } catch (e) {
      setCatalogError(e.message || 'No se pudo cargar el inventario de anuncios.');
    } finally {
      setCatalogLoading(false);
    }
  }

  const managerUrl = accountId && /^[0-9]+$/.test(String(accountId)) ?
    'https://adsmanager.facebook.com/adsmanager/manage/ads?act=' + accountId : '';

  return <div className="marketing-workbench">
    <section className="mgw-panel">
      <WorkbenchHead number="05" title="Indicadores de tráfico" description="Diferenciar todos los clics de clics en enlaces" />
      <div className="mgw-kpis">
        <div><span>Clics en enlaces</span><strong>{linked ? count(linked.clicks) : '—'}</strong><small>Meta inline_link_clicks</small></div>
        <div><span>CTR de enlaces</span><strong>{linked?.ctr != null ? percentage(linked.ctr) : '—'}</strong><small>Clics de enlace / impresiones</small></div>
        <div><span>CPC de enlaces</span><strong>{linked?.cpc != null ? amount(linked.cpc, currency) : '—'}</strong><small>Inversión / clics en enlaces</small></div>
      </div>
      <p className="mgw-muted">Son métricas distintas de CTR y CPC de todos los clics. Los valores ausentes se muestran como «—», nunca como cero supuesto.</p>
    </section>

    <MetaDailyTrend range={range} currency={currency} />

    <section className="mgw-panel">
      <WorkbenchHead number="07" title="Oportunidades para optimizar" description="Señales de tus propios datos; propuestas, no acciones automáticas" />
      <div className="mgw-panel-toolbar">
        <p>Los cambios de creatividades o presupuesto se prueban y aprueban individualmente.</p>
        <select aria-label="Filtrar recomendaciones" value={focus} onChange={(e) => setFocus(e.target.value)}>
          <option value="all">Todas las señales</option>
          <option value="revisar">Prioridad: revisar</option>
          <option value="hipótesis">Hipótesis</option>
          <option value="información">Información general</option>
        </select>
      </div>
      <div className="mgw-signals">{filteredSignals.map((item, i) =>
        <article key={i} className="mgw-signal">
          <span className="mgw-eyebrow">{item.level} · {item.campaign}</span>
          <h3>{item.title}</h3>
          <p>{item.evidence}</p>
          <small>{item.action}</small>
        </article>)}</div>
    </section>

    <section className="mgw-panel">
      <WorkbenchHead number="08" title="Mis anuncios" description="Creatividades reales con actividad en el período seleccionado" />
      {unavailableAdLevel ? <p className="mgw-alert">Meta no entregó el desglose de anuncios en este período. No se muestran resultados incompletos como si fueran cero.</p> :
        ads.length ? <>
          <div className="mgw-table-wrap"><table className="mgw-table">
            <thead><tr><th>Anuncio</th><th>Campaña / conjunto</th><th>Inversión</th><th>Leads Meta</th><th>CPL</th><th>CTR general</th><th>Creativo</th></tr></thead>
            <tbody>{visibleAds.map((row) =>
              <tr key={row.id}>
                <td><strong>{row.name}</strong><small>{row.id}</small></td>
                <td>{row.campaignName}<small>{row.adsetName}</small></td>
                <td>{amount(row.metrics.spend, currency)}</td>
                <td>{count(row.metrics.leads)}</td>
                <td>{row.metrics.leads ? amount(row.metrics.cpl, currency) : '—'}</td>
                <td>{percentage(row.metrics.ctr)}</td>
                <td><button type="button" className="mgw-btn is-secondary" onClick={() => inspect(row.id)}>Ver pieza</button></td>
              </tr>
            )}</tbody>
          </table></div>
          {!showAll && ads.length > 25 ? <button type="button" className="mgw-btn is-secondary" onClick={() => setShowAll(true)}>Ver los {ads.length} anuncios</button> : null}
        </> : <p className="mgw-muted">No hay anuncios con actividad publicitaria registrada en este período.</p>}
      <div className="mgw-catalog">
        <div className="mgw-panel-toolbar">
          <p>El listado anterior incluye anuncios con actividad en el período. También podés consultar el inventario completo (activos y pausados).</p>
          <button type="button" className="mgw-btn is-secondary" onClick={loadCatalog} disabled={catalogLoading}>
            {catalogLoading ? 'Consultando inventario…' : catalog ? 'Actualizar inventario' : 'Consultar todos los anuncios'}
          </button>
        </div>
        {catalogError ? <p role="alert" className="mgw-alert">{catalogError}</p> : null}
        {catalogStale ? <p className="mgw-alert">Inventario en caché: Meta limitó las consultas recientes.</p> : null}
        {catalog ? <details open className="mgw-method">
          <summary>Inventario: {catalog.length} anuncios</summary>
          <div className="mgw-table-wrap"><table className="mgw-table">
            <thead><tr><th>Nombre</th><th>Estado actual</th><th>ID</th><th>Detalle</th></tr></thead>
            <tbody>{catalog.slice(0, 250).map((item) => <tr key={item.id}>
              <td>{item.name}</td><td>{item.effectiveStatus || item.status || '—'}</td>
              <td>{item.id}</td><td><button type="button" className="mgw-btn is-secondary" onClick={() => inspect(item.id)}>Ver pieza</button></td>
            </tr>)}</tbody>
          </table></div>
          {catalog.length > 250 ? <p className="mgw-muted">Se muestran los primeros 250 anuncios del inventario.</p> : null}
        </details> : null}
      </div>
      {selectedAdId ? <div className="mgw-preview">
        <div className="mgw-panel-toolbar"><h3>Vista del anuncio {selectedAdId}</h3><button type="button" className="mgw-close" onClick={() => { setSelectedAdId(''); setPreview(null); }}>Cerrar</button></div>
        {previewLoading ? <p>Consultando creativo en Meta…</p> : null}
        {previewError ? <p role="alert" className="mgw-alert">{previewError}</p> : null}
        {preview ? <div className="mgw-preview-body">
          {preview.thumbnailUrl ? <img src={preview.thumbnailUrl} alt={'Miniatura del anuncio ' + preview.name} loading="lazy" referrerPolicy="no-referrer" /> : null}
          <div>
            <h3>{preview.name}</h3>
            <p><strong>Estado:</strong> {preview.effectiveStatus || 'No disponible'}</p>
            {preview.headline ? <p><strong>Titular:</strong> {preview.headline}</p> : null}
            {preview.primaryText ? <p className="mgw-copy">{preview.primaryText}</p> : null}
            {preview.previewLimited ? <p className="mgw-muted">Meta no devolvió texto ni miniatura de este creativo. Algunos anuncios dinámicos solo se pueden ver completos en Ads Manager.</p> : null}
            {managerUrl ? <a href={managerUrl} target="_blank" rel="noopener noreferrer">Abrir Administrador de Anuncios ↗</a> : null}
          </div>
        </div> : null}
      </div> : null}
    </section>

    <MetaCreativeStudio />
  </div>;
}
